import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryParam } from '@cbgms/base/hooks/use-query-param/use-query-param'
import { DuplicationData, OnboardingDuplicationCheckType, OnboardingStepKey } from '@cbgms/api'
import { Box, Card, CardActions, CardContent, Divider, Typography } from '@mui/material'
import { Alert, Button } from '@carsys/ui'
import { formatAdressFull } from '@cbgms/base/utils/address'
import { CountryCode } from '@carsys/enums/country-code'
import { useOnboardingContext } from 'app/auth/components/onboarding/use-onboarding-context'
import { useDispatch } from 'react-redux'
import { stepOutputProps } from 'app/auth/components/onboarding/steps/SignupCredentialsStep/use-signup-credentials-step'
import { inviteUserToShop } from 'app/auth/state/actions'
import { ApiError, generateApiError } from 'utils/Request/Error'
import * as routing from 'app/auth/components/onboarding/constants/routes'
import { useHistory } from 'react-router-dom'

export const DuplicationFoundOnType: React.FC<{ checkType: OnboardingDuplicationCheckType }> = ({ checkType }) => {
  const { t } = useTranslation()
  let message = ''
  switch (checkType) {
    case OnboardingDuplicationCheckType.ShopAddress:
      message = t('Auth.OnboardingDuplicateOnAddress')
      break
    case OnboardingDuplicationCheckType.ShopPhoneNumber:
      message = t('Auth.OnboardingDuplicateOnPhoneNumber')
      break
    case OnboardingDuplicationCheckType.ShopVat:
      message = t('Auth.OnboardingDuplicateOnVat')
      break
    case OnboardingDuplicationCheckType.ShopEmail:
      message = t('Auth.OnboardingDuplicateOnShopEmail')
      break
    case OnboardingDuplicationCheckType.OrderAccount:
      message = t('Auth.OnboardingDuplicateOnOrderAccount')
      break
    case OnboardingDuplicationCheckType.ShopMGID:
      message = 'Duplicate MGID found. This MGID is already associated with another shop.'
      break
  }

  if (!message) {
    return null
  }
  return (
    <Box sx={{ mb: 1 }}>
      <Alert severity='info'>{message}</Alert>
    </Box>
  )
}

export const ErrorMessage: React.FC<{ error: ApiError | null }> = ({ error }) => {
  const { t } = useTranslation()
  const message = error?.errorMessage()
  if (!message) {
    return null
  }
  return (
    <Box sx={{ mb: 1 }}>
      <Alert severity='error' title={t('Auth.OnboardingErrorTitleInviteToShop')}>
        {message}
      </Alert>
    </Box>
  )
}

const CardDataLine: React.FC<{ title: string; value?: string }> = ({ title, value }) => {
  if (!value) {
    return null
  }
  return (
    <Typography component='div' sx={{ mb: 1 }} variant='body2'>
      <Box sx={{ fontWeight: 'bold' }}>{title}</Box>
      <Box>{value}</Box>
    </Typography>
  )
}

const CardFoundShop: React.FC<{ handleJoinShopClick: () => void; duplicationData: DuplicationData | null }> = ({
  handleJoinShopClick,
  duplicationData
}) => {
  const { t } = useTranslation()
  const shop = duplicationData?.dupe
  if (!shop) {
    return null
  }

  return (
    <Card sx={theme => ({ width: '100%', display: 'inline-block', border: `1px solid ${theme.palette.LKQGrey.border.modest}` })}>
      <CardContent>
        <Typography sx={{ fontSize: 14 }} color='text.secondary' gutterBottom>
          {t('Auth.OnboardingFoundDuplicateShopTitle')}
        </Typography>
        <Typography sx={{ mb: 1 }} variant='h5' component='div'>
          {shop.Name}
        </Typography>
        <CardDataLine
          title={t('Auth.OnboardingFoundDuplicateAddress')}
          value={formatAdressFull(shop, 'Visitor', shop.VisitorCountry as CountryCode)}
        />
        <CardDataLine title={t('Auth.OnboardingFoundDuplicateVATNumber')} value={shop.VATIdentification} />
        <CardDataLine title={t('Auth.OnboardingFoundDuplicatePhoneNumber')} value={shop.PhoneNumber} />
        <CardDataLine title={t('Auth.OnboardingFoundDuplicateEmail')} value={shop.EmailAddress} />
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end' }}>
        <Button onClick={handleJoinShopClick} data-testid='btn-join-shop'>
          {t('Auth.OnboardingButtonJoinOtherShop')}
        </Button>
      </CardActions>
    </Card>
  )
}

export const InviteUserToOtherShop = () => {
  const { t } = useTranslation()
  const navigation = useHistory()
  const [error, setError] = useState<ApiError | null>(null)
  const [checkType] = useQueryParam<OnboardingDuplicationCheckType>('checkType')
  const { duplicationData, ticketUUID, steps, basePath } = useOnboardingContext()
  const userCredentials = useMemo(() => {
    return (steps.find(s => s.step === OnboardingStepKey.SignUpCredentials) as stepOutputProps | undefined)?.userCredentials
  }, [steps])
  const dispatch = useDispatch()

  const handleNotMyShopClick = () => {
    const newLocation = navigation.location.pathname.replace(
      routing.pathInviteUserToOtherShop(''),
      routing.urlDuplicationError('', checkType)
    )
    navigation.push(newLocation)
  }
  const handleJoinShopClick = async () => {
    const dupe = duplicationData?.dupe
    if (!dupe || !userCredentials) {
      setError(generateApiError('ErrAccountEmpty'))
      return
    }
    try {
      const result = await dispatch(
        inviteUserToShop({
          ShopID: dupe.ID,
          CompanyUUID: dupe.CompanyUUID,
          UserEmail: userCredentials.EmailAddress,
          FirstName: userCredentials.FirstName,
          LastName: userCredentials.Name,
          NamePrefix: userCredentials.NamePrefix,
          TicketUUID: ticketUUID || ''
        })
      )
      if (result.Data) {
        navigation.push(routing.urlSignUpFinished(basePath))
      }
    } catch (e) {
      setError(generateApiError(e))
    }
  }

  return (
    <Box textAlign='left'>
      <Box>
        <Box mb={2}>
          <Typography variant='h4'> {t('Auth.OnboardingTitleDuplication')}</Typography>
        </Box>
        <Box mb={1}>
          <DuplicationFoundOnType checkType={checkType} />
          <ErrorMessage error={error} />
          <Box>
            <Typography variant='subtitle1'>{t('Auth.OnboardingDuplicateInviteToOtherShop')}</Typography>
          </Box>
          {userCredentials?.EmailAddress && (
            <Box>
              <Typography component='div' variant='subtitle1'>
                {t('Auth.OnboardingDuplicateInviteWithUser')} <span style={{ fontWeight: 'bold' }}>{userCredentials?.EmailAddress}</span>
              </Typography>
            </Box>
          )}
        </Box>
        <Divider />
        <Box my={3} display='flex' justifyContent='center'>
          <CardFoundShop handleJoinShopClick={handleJoinShopClick} duplicationData={duplicationData} />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant='outlined' onClick={handleNotMyShopClick} data-testid='btn-not-my-shop'>
            {t('Auth.OnboardingButtonCancelNotMyGarage')}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}
export default InviteUserToOtherShop
